import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  getFirestore,
  collection,
  getDocs,
  addDoc,
  doc,
  updateDoc,
  deleteDoc,
  query,
  where,
} from "firebase/firestore";
import Modal from "./Modal";
import "./styles/CatalogsManagerModal.css";

function CatalogsManagerModal() {
  const [showModal, setShowModal] = useState(false);
  const [catalogs, setCatalogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dragging, setDragging] = useState(null);
  const [selected, setSelected] = useState({
    type: null,
    parentId: null,
    index: null,
  });
  const db = getFirestore();

  // Refs to store original names while editing (so onBlur gets the old name)
  const originalCatalogNamesRef = useRef({});
  const originalCollectionNamesRef = useRef({}); // { [catalogId]: { [index]: oldName } }

  // === Fetch catalogs ===
  const fetchCatalogs = useCallback(async () => {
    try {
      const snap = await getDocs(collection(db, "catalogs"));
      const cats = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0));
      setCatalogs(cats);
    } catch (err) {
      console.error("Error fetching catalogs:", err);
    } finally {
      setLoading(false);
    }
  }, [db]);

  useEffect(() => {
    fetchCatalogs();
  }, [fetchCatalogs]);

  // === Add catalog inline ===
  const [newCatalogName, setNewCatalogName] = useState("");

  const addCatalog = async () => {
    if (!newCatalogName.trim()) return;
    const newDoc = await addDoc(collection(db, "catalogs"), {
      catalogName: newCatalogName.trim(),
      collections: [],
      sortIndex: catalogs.length,
    });
    setCatalogs((prev) => [
      ...prev,
      {
        id: newDoc.id,
        catalogName: newCatalogName.trim(),
        collections: [],
        sortIndex: prev.length,
      },
    ]);
    setNewCatalogName("");
  };

  // === helper: run multiple queries and combine unique docs ===
  const collectDocsFromQueries = async (queries) => {
    const docsMap = new Map();
    for (const q of queries) {
      try {
        const snap = await getDocs(q);
        snap.docs.forEach((d) => {
          if (!docsMap.has(d.id)) docsMap.set(d.id, d);
        });
      } catch (err) {
        console.error("Query error:", err);
      }
    }
    return Array.from(docsMap.values());
  };

  // === Update product references when renaming catalog ===
  // This will update docs where either catalogName == oldName OR updatedCatalogName == oldName
  const updateProductsCatalogName = async (oldName, newName) => {
    if (!oldName) return;
    const productsRef = collection(db, "catalogProducts");

    const q1 = query(productsRef, where("catalogName", "==", oldName));
    const q2 = query(productsRef, where("updatedCatalogName", "==", oldName));

    const docs = await collectDocsFromQueries([q1, q2]);

    const updates = docs.map((d) =>
      updateDoc(doc(db, "catalogProducts", d.id), {
        catalogName: newName,
        updatedCatalogName: newName,
      })
    );

    await Promise.all(updates);
  };

  // === Update product references when renaming collection ===
  // This will update docs where (catalogName==catalogName AND collectionName==oldName)
  // OR (catalogName==catalogName AND updatedCollectionName==oldName)
  const updateProductsCollectionName = async (
    catalogName,
    oldName,
    newName
  ) => {
    if (!oldName) return;
    const productsRef = collection(db, "catalogProducts");

    const q1 = query(
      productsRef,
      where("catalogName", "==", catalogName),
      where("collectionName", "==", oldName)
    );

    const q2 = query(
      productsRef,
      where("catalogName", "==", catalogName),
      where("updatedCollectionName", "==", oldName)
    );

    const docs = await collectDocsFromQueries([q1, q2]);

    const updates = docs.map((d) =>
      updateDoc(doc(db, "catalogProducts", d.id), {
        collectionName: newName,
        updatedCollectionName: newName,
      })
    );

    await Promise.all(updates);
  };

  // === Rename catalog (local UI only while editing) ===
  const renameCatalog = (catalogId, newName) => {
    setCatalogs((prev) =>
      prev.map((c) => (c.id === catalogId ? { ...c, catalogName: newName } : c))
    );
  };

  // === Save catalog name (commits rename to firestore and updates products) ===
  const saveCatalogName = async (catalogId, newName, oldName) => {
    const trimmedNew = newName?.trim?.();
    const trimmedOld = oldName?.trim?.();
    if (!trimmedNew || trimmedNew === trimmedOld) return;

    try {
      await updateDoc(doc(db, "catalogs", catalogId), {
        catalogName: trimmedNew,
      });
      await updateProductsCatalogName(trimmedOld, trimmedNew);
    } catch (err) {
      console.error("Error saving catalog name:", err);
    }
  };

  // === Delete catalog ===
  const deleteCatalog = async (catalogId) => {
    if (!window.confirm("Delete this catalog and all its collections?")) return;
    await deleteDoc(doc(db, "catalogs", catalogId));
    setCatalogs((prev) => prev.filter((c) => c.id !== catalogId));
  };

  // === Add collection inline ===
  const addCollection = async (catalogId, name) => {
    if (!name.trim()) return;
    const catalog = catalogs.find((c) => c.id === catalogId);
    const newCollections = [...(catalog.collections || []), name.trim()];
    await updateDoc(doc(db, "catalogs", catalogId), {
      collections: newCollections,
    });
    setCatalogs((prev) =>
      prev.map((c) =>
        c.id === catalogId ? { ...c, collections: newCollections } : c
      )
    );
  };

  // === Rename collection (local UI only while editing) ===
  const renameCollection = (catalogId, index, newName) => {
    setCatalogs((prev) =>
      prev.map((c) => {
        if (c.id === catalogId) {
          const newCols = [...c.collections];
          newCols[index] = newName;
          return { ...c, collections: newCols };
        }
        return c;
      })
    );
  };

  // === Save collection name (commit) ===
  const saveCollectionNames = async (
    catalogId,
    collections,
    catalogName,
    oldName,
    newName
  ) => {
    const trimmedNew = newName?.trim?.();
    const trimmedOld = oldName?.trim?.();
    if (!trimmedNew || trimmedNew === trimmedOld) return;

    try {
      await updateDoc(doc(db, "catalogs", catalogId), { collections });
      await updateProductsCollectionName(catalogName, trimmedOld, trimmedNew);
    } catch (err) {
      console.error("Error saving collection name:", err);
    }
  };

  // === Delete collection ===
  const deleteCollection = async (catalogId, index) => {
    if (!window.confirm("Delete this collection?")) return;
    const catalog = catalogs.find((c) => c.id === catalogId);
    const newCollections = catalog.collections.filter((_, i) => i !== index);
    await updateDoc(doc(db, "catalogs", catalogId), {
      collections: newCollections,
    });
    setCatalogs((prev) =>
      prev.map((c) =>
        c.id === catalogId ? { ...c, collections: newCollections } : c
      )
    );
  };

  // === Drag & Drop ===
  const handleDragStart = (type, parentId, index) => {
    setDragging({ type, parentId, index });
  };

  const handleDragOver = (e) => e.preventDefault();

  const handleDrop = (type, parentId, index) => {
    if (!dragging) return;
    if (dragging.type === type && dragging.parentId === parentId) {
      if (type === "catalog") {
        const updated = [...catalogs];
        const [moved] = updated.splice(dragging.index, 1);
        updated.splice(index, 0, moved);
        setCatalogs(updated);
      } else if (type === "collection") {
        const updated = catalogs.map((c) => {
          if (c.id === parentId) {
            const newCols = [...c.collections];
            const [moved] = newCols.splice(dragging.index, 1);
            newCols.splice(index, 0, moved);
            return { ...c, collections: newCols };
          }
          return c;
        });
        setCatalogs(updated);
      }
    }
    setDragging(null);
  };

  // === Keyboard reorder ===
  const handleKeyDown = (e) => {
    if (!selected.type) return;
    const { type, parentId, index } = selected;
    const move = (delta) => {
      if (type === "catalog") {
        const newIndex = index + delta;
        if (newIndex < 0 || newIndex >= catalogs.length) return;
        const updated = [...catalogs];
        const [moved] = updated.splice(index, 1);
        updated.splice(newIndex, 0, moved);
        setCatalogs(updated);
        setSelected({ type, parentId, index: newIndex });
      } else {
        const catalog = catalogs.find((c) => c.id === parentId);
        const newCols = [...catalog.collections];
        const newIndex = index + delta;
        if (newIndex < 0 || newIndex >= newCols.length) return;
        const [moved] = newCols.splice(index, 1);
        newCols.splice(newIndex, 0, moved);
        setCatalogs((prev) =>
          prev.map((c) =>
            c.id === parentId ? { ...c, collections: newCols } : c
          )
        );
        setSelected({ type, parentId, index: newIndex });
      }
    };
    if (e.key === "ArrowUp") move(-1);
    if (e.key === "ArrowDown") move(1);
  };

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  });

  // === Save order ===
  const saveOrder = async () => {
    for (let i = 0; i < catalogs.length; i++) {
      const c = catalogs[i];
      await updateDoc(doc(db, "catalogs", c.id), {
        sortIndex: i,
        catalogName: c.catalogName,
        collections: c.collections,
      });
    }
    alert("Changes saved successfully.");
  };

  return (
    <>
      <span className="open-catalog-btn" onClick={() => setShowModal(true)}>
        Manage Catalogs
      </span>

      <Modal
        show={showModal}
        onClose={() => setShowModal(false)}
        title="Catalogs & Collections"
      >
        <div className="catalogs-container">
          {loading ? (
            <p>Loading...</p>
          ) : (
            <div>
              <div className="add-catalog-row mb-3">
                <input
                  type="text"
                  placeholder="New catalog name..."
                  className="catalog-input new"
                  value={newCatalogName}
                  onChange={(e) => setNewCatalogName(e.target.value)}
                />
                <button className="add-btn" onClick={addCatalog}>
                  Add
                </button>
              </div>

              {catalogs.map((catalog, catIdx) => (
                <div
                  key={catalog.id}
                  className={`catalog-card ${
                    selected.type === "catalog" && selected.index === catIdx
                      ? "selected"
                      : ""
                  }`}
                  draggable
                  onDragStart={() => handleDragStart("catalog", null, catIdx)}
                  onDragOver={handleDragOver}
                  onDrop={() => handleDrop("catalog", null, catIdx)}
                >
                  <div className="catalog-header">
                    <span className="hierarchy-label">📁 Catalog</span>

                    {/* Catalog input - store original on focus */}
                    <input
                      type="text"
                      value={catalog.catalogName}
                      className="catalog-input"
                      onChange={(e) =>
                        renameCatalog(catalog.id, e.target.value)
                      }
                      onFocus={() =>
                        (originalCatalogNamesRef.current[catalog.id] =
                          catalog.catalogName)
                      }
                      onBlur={(e) =>
                        saveCatalogName(
                          catalog.id,
                          e.target.value,
                          originalCatalogNamesRef.current[catalog.id] ??
                            catalog.catalogName
                        )
                      }
                    />
                    <button
                      className="delete-btn"
                      onClick={() => deleteCatalog(catalog.id)}
                    >
                      🗑️
                    </button>
                  </div>

                  <ul className="collections-list">
                    <InlineAdd
                      placeholder="Add new collection..."
                      onAdd={(name) => addCollection(catalog.id, name)}
                    />
                    {catalog.collections.map((col, colIdx) => (
                      <li
                        key={colIdx}
                        className={`collection-item ${
                          selected.type === "collection" &&
                          selected.parentId === catalog.id &&
                          selected.index === colIdx
                            ? "selected"
                            : ""
                        }`}
                        draggable
                        onDragStart={() =>
                          handleDragStart("collection", catalog.id, colIdx)
                        }
                        onDragOver={handleDragOver}
                        onDrop={() =>
                          handleDrop("collection", catalog.id, colIdx)
                        }
                      >
                        <span className="hierarchy-label sub">
                          ↳ Collection
                        </span>

                        {/* Collection input */}
                        <input
                          type="text"
                          className="collection-input"
                          value={col}
                          onChange={(e) =>
                            renameCollection(catalog.id, colIdx, e.target.value)
                          }
                          onFocus={() => {
                            if (
                              !originalCollectionNamesRef.current[catalog.id]
                            ) {
                              originalCollectionNamesRef.current[catalog.id] =
                                {};
                            }
                            originalCollectionNamesRef.current[catalog.id][
                              colIdx
                            ] = col;
                          }}
                          onBlur={(e) =>
                            saveCollectionNames(
                              catalog.id,
                              catalog.collections,
                              catalog.catalogName,
                              originalCollectionNamesRef.current[catalog.id]?.[
                                colIdx
                              ] ?? col,
                              e.target.value
                            )
                          }
                        />
                        <button
                          className="delete-btn small"
                          onClick={() => deleteCollection(catalog.id, colIdx)}
                        >
                          ×
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="footer-buttons">
          <button className="btn-secondary" onClick={() => setShowModal(false)}>
            Close
          </button>
          <button className="btn-primary" onClick={saveOrder}>
            Save Changes
          </button>
        </div>
      </Modal>
    </>
  );
}

// Inline Add Input Component
function InlineAdd({ placeholder, onAdd }) {
  const [value, setValue] = useState("");
  const inputRef = useRef();

  const submit = () => {
    if (!value.trim()) return;
    onAdd(value.trim());
    setValue("");
    inputRef.current.focus();
  };

  return (
    <div className="inline-add mb-3">
      <input
        ref={inputRef}
        type="text"
        value={value}
        placeholder={placeholder}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && submit()}
      />
      <button className="add-btn small" onClick={submit}>
        Add
      </button>
    </div>
  );
}

export default CatalogsManagerModal;
